// client/src/pages/BillingPage.jsx
// Billing & plan. This is the SINGLE billing surface in the app — Settings →
// Billing renders it with embedded={true}, and the legacy `#billing` route
// redirects into that tab. It previously had a hardcoded twin inside
// SettingsPage that always showed "$49/month" regardless of the account's
// actual plan; that twin is gone.
//
// Admin gating: the Settings nav hides this tab from members, and the server
// gates /api/billing/portal + /api/billing/checkout to owner/admin. This
// component additionally hides the action buttons for members, so a member
// who reaches it some other way sees plan/usage info but no way to act.
//
// embedded=true drops the page padding and the big page heading, because
// SettingsPage supplies its own card, padding, and heading.

import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;
const PLAN_NAMES = { starter: 'Starter', growth: 'Growth', enterprise: 'Enterprise', standard: 'Legacy' };
const PLAN_PRICES = { starter: 59, growth: 79, enterprise: 99, standard: 49 };

export default function BillingPage({ getToken, showToast, navigate, embedded = false }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  // Cancel flow. `cancelStep` is null when closed, 'reason' while asking why,
  // 'confirm' on the final are-you-sure. Split into two steps deliberately:
  // the reason prompt is also the last natural place to change your mind.
  const [cancelStep,   setCancelStep]   = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote,   setCancelNote]   = useState('');
  const [cancelBusy,   setCancelBusy]   = useState(false);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const data = await api.withToken(token).getBillingStatus();
      setStatus(data);
    } catch (e) {
      showToast('Failed to load billing: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [getToken, showToast]);

  useEffect(() => { load(); }, [load]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const token = await getToken();
      const { url } = await api.withToken(token).openPortal();
      window.location.href = url;
    } catch (e) {
      showToast('Failed to open billing portal: ' + e.message, 'error');
      setPortalLoading(false);
    }
  };

  const [seatBusy, setSeatBusy] = useState(false);

  const changeSeats = async (next) => {
    setSeatBusy(true);
    try {
      const token = await getToken();
      const r = await api.withToken(token).setExtraSeats(next);
      const delta = next - (status?.seats?.extra || 0);
      showToast(
        delta > 0
          ? `Seat added — $${delta * 10}/mo, prorated for the rest of this period.`
          : `Seat${delta < -1 ? 's' : ''} released — you'll see a credit on your next invoice.`,
        'success'
      );
      await load();
      return r;
    } catch (e) {
      // 409 = they'd be paying for fewer seats than people on the team.
      showToast(e.message, 'error');
    } finally { setSeatBusy(false); }
  };

  const doCancel = async () => {
    setCancelBusy(true);
    try {
      const token = await getToken();
      const r = await api.withToken(token).cancelSubscription({ reason: cancelReason, feedback: cancelNote });
      const until = r?.accessUntil ? new Date(r.accessUntil).toLocaleDateString() : 'the end of your billing period';
      showToast(`Subscription canceled. You have full access until ${until}.`, 'success');
      setCancelStep(null); setCancelReason(''); setCancelNote('');
      await load();
    } catch (e) {
      showToast('Could not cancel: ' + e.message, 'error');
    } finally { setCancelBusy(false); }
  };

  const doReactivate = async () => {
    setCancelBusy(true);
    try {
      const token = await getToken();
      await api.withToken(token).reactivateSubscription();
      showToast('Subscription resumed — your plan will renew as normal.', 'success');
      await load();
    } catch (e) {
      showToast('Could not resume: ' + e.message, 'error');
    } finally { setCancelBusy(false); }
  };

  if (loading) return <div style={{ padding: 40, color: '#94A3B8' }}>Loading billing…</div>;
  if (!status) return null;

  const usagePct = status.bundleSearchesQuota > 0
    ? Math.min(100, (status.bundleSearchesUsed / status.bundleSearchesQuota) * 100)
    : 0;
  const usageColor = usagePct >= 90 ? '#EF4444' : usagePct >= 75 ? '#F59E0B' : G;

  const subStatusLabel = {
    trialing: { text: `Trial · ${status.trialDaysRemaining ?? '?'} day${status.trialDaysRemaining === 1 ? '' : 's'} remaining`, color: G },
    active:   { text: 'Active', color: G },
    past_due: { text: 'Past due — update payment to restore access', color: '#EF4444' },
    canceled: { text: 'Canceled — access ends ' + (status.currentPeriodEnd ? new Date(status.currentPeriodEnd).toLocaleDateString() : 'soon'), color: '#F59E0B' },
  }[status.subscriptionStatus] || {
    // Legacy / pre-tier accounts have no subscriptionStatus row from Stripe.
    // Show a clear status instead of "Unknown" — they're functional, just on
    // a grandfathered plan until they pick a current tier.
    text:  status.plan === 'standard' ? 'Legacy plan — pick a current plan to update' : 'Inactive',
    color: status.plan === 'standard' ? '#64748B' : '#94A3B8',
  };

  const isAdmin = status.role === 'owner' || status.role === 'admin';
  return (
    <div style={embedded ? {} : { padding: 24, maxWidth: 880 }}>
      {!embedded && <>
        <div style={{ fontSize: 22, fontWeight: 600, color: BRAND.colors.dark, marginBottom: 4 }}>Billing &amp; plan</div>
        <div style={{ fontSize: 13, color: '#64748B', marginBottom: 24 }}>Manage your subscription, payment methods, and usage</div>
      </>}

      {/* Past-due banner */}
      {status.subscriptionStatus === 'past_due' && (
        <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 10, padding: 14, marginBottom: 18, color: '#991B1B', fontSize: 13 }}>
          <strong>Payment failed.</strong> Your account access is paused until billing is updated. Click "Manage billing" below to update your card.
        </div>
      )}

      {/* No-real-plan banner. Triggered when account is on the legacy "standard"
          plan (predates the tier system) or has no Stripe customer linked yet.
          Distributors in this state should pick a real plan. */}
      {(status.plan === 'standard' || !status.hasPaymentMethod) && isAdmin && (
        <div style={{ background: `${G}10`, border: `1px solid ${G}33`, borderRadius: 10, padding: 14, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#059669', marginBottom: 2 }}>
              Choose your plan
            </div>
            <div style={{ fontSize: 12, color: '#475569' }}>
              {status.plan === 'standard'
                ? 'Your account is on a legacy plan. Pick a current plan to unlock all features and clarify your billing.'
                : 'You don\'t have a subscription set up yet. Pick a plan to activate full access.'}
            </div>
          </div>
          <button onClick={() => navigate('pickplan')}
            style={{ background: G, border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 500, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            Pick a plan →
          </button>
        </div>
      )}

      {/* Plan & subscription status */}
      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Current plan</div>
            <div style={{ fontSize: 22, fontWeight: 600, color: BRAND.colors.dark }}>
              {PLAN_NAMES[status.plan] || status.plan} <span style={{ fontSize: 14, color: '#94A3B8', fontWeight: 400 }}>· ${PLAN_PRICES[status.plan] || '?'}/mo</span>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: subStatusLabel.color, fontWeight: 500 }}>
              {subStatusLabel.text}
            </div>
            {status.currentPeriodEnd && status.subscriptionStatus !== 'canceled' && (
              <div style={{ marginTop: 4, fontSize: 11, color: '#94A3B8' }}>
                Next billing: {new Date(status.currentPeriodEnd).toLocaleDateString()}
              </div>
            )}
          </div>
          {isAdmin && (
            <button onClick={openPortal} disabled={portalLoading || !status.hasPaymentMethod}
              style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 16px', fontSize: 12, color: BRAND.colors.dark, cursor: (portalLoading || !status.hasPaymentMethod) ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
              {portalLoading ? 'Opening…' : 'Manage billing →'}
            </button>
          )}
        </div>
      </div>

      {/* Cancel / resume. Admin-only, and hidden entirely for accounts with no
          live subscription to act on. */}
      {isAdmin && status.subscriptionStatus !== 'canceled' && (
        <div style={{ background:'#fff', border:'1px solid #E2E8F0', borderRadius:12, padding:20, marginBottom:16 }}>
          {status.cancelScheduled ? (
            <>
              <div style={{ fontSize:13, fontWeight:600, color:'#92400E', marginBottom:4 }}>Cancellation scheduled</div>
              <div style={{ fontSize:12, color:'#64748B', marginBottom:14, lineHeight:1.6 }}>
                You keep full access until {status.currentPeriodEnd ? new Date(status.currentPeriodEnd).toLocaleDateString() : 'the end of your billing period'}.
                Nothing is deleted — you can resume any time before then and carry on as if nothing happened.
              </div>
              <button onClick={doReactivate} disabled={cancelBusy}
                style={{ background:G, border:'none', borderRadius:8, padding:'9px 18px', color:'#fff',
                  fontSize:13, fontWeight:600, cursor: cancelBusy ? 'wait' : 'pointer', fontFamily:'inherit' }}>
                {cancelBusy ? 'Resuming…' : 'Resume my subscription'}
              </button>
            </>
          ) : cancelStep === null ? (
            <button onClick={() => setCancelStep('reason')}
              style={{ background:'none', border:'none', padding:0, color:'#94A3B8', fontSize:12,
                cursor:'pointer', fontFamily:'inherit', textDecoration:'underline' }}>
              Cancel subscription
            </button>
          ) : cancelStep === 'reason' ? (
            <>
              <div style={{ fontSize:14, fontWeight:600, color:BRAND.colors.dark, marginBottom:4 }}>Before you go — what's driving this?</div>
              <div style={{ fontSize:12, color:'#64748B', marginBottom:14 }}>
                Optional, but it genuinely shapes what we build next.
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:12 }}>
                {['Too expensive',
                  'Missing a feature I need',
                  'Too hard to use',
                  'Switched to another tool',
                  'Not using it enough',
                  'Only needed it temporarily',
                  'Other'].map(r => (
                  <label key={r} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#334155', cursor:'pointer' }}>
                    <input type="radio" name="cancelReason" value={r} checked={cancelReason === r}
                      onChange={() => setCancelReason(r)} style={{ cursor:'pointer' }}/>
                    {r}
                  </label>
                ))}
              </div>
              <textarea value={cancelNote} onChange={e => setCancelNote(e.target.value)}
                placeholder="Anything else you'd want us to know? (optional)"
                rows={3}
                style={{ width:'100%', border:'1px solid #E2E8F0', borderRadius:8, padding:'8px 10px',
                  fontSize:13, fontFamily:'inherit', outline:'none', resize:'vertical', marginBottom:14,
                  color:'#0F172A', boxSizing:'border-box' }}/>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <button onClick={() => setCancelStep('confirm')}
                  style={{ background:'#fff', border:'1px solid #FCA5A5', borderRadius:8, padding:'9px 16px',
                    color:'#B91C1C', fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
                  Continue to cancel
                </button>
                <button onClick={() => { setCancelStep(null); setCancelReason(''); setCancelNote(''); }}
                  style={{ background:G, border:'none', borderRadius:8, padding:'9px 18px', color:'#fff',
                    fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                  Never mind, keep my plan
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize:14, fontWeight:600, color:BRAND.colors.dark, marginBottom:8 }}>Cancel your subscription?</div>
              <div style={{ fontSize:12, color:'#64748B', marginBottom:14, lineHeight:1.6 }}>
                You'll keep full access until {status.currentPeriodEnd ? new Date(status.currentPeriodEnd).toLocaleDateString() : 'the end of your current billing period'} — no refund is issued for the remainder, and you won't be charged again.
                After that date the app locks and you'll need a plan to get back in.
                <br/><br/>
                <strong style={{ color:'#334155' }}>Your data isn't deleted.</strong> Presentations, purchase orders, clients, and decorator matrices all stay exactly as they are, and resubscribing restores access to everything.
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <button onClick={doCancel} disabled={cancelBusy}
                  style={{ background:'#DC2626', border:'none', borderRadius:8, padding:'9px 16px', color:'#fff',
                    fontSize:13, fontWeight:600, cursor: cancelBusy ? 'wait' : 'pointer', fontFamily:'inherit' }}>
                  {cancelBusy ? 'Canceling…' : 'Yes, cancel my subscription'}
                </button>
                <button onClick={() => setCancelStep('reason')} disabled={cancelBusy}
                  style={{ background:'none', border:'1px solid #E2E8F0', borderRadius:8, padding:'9px 16px',
                    color:'#334155', fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
                  Go back
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* AI bundle usage */}
      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>AI Bundle Builder usage</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div style={{ fontSize: 22, fontWeight: 600, color: BRAND.colors.dark }}>
            {status.bundleSearchesUsed}<span style={{ fontSize: 14, color: '#94A3B8', fontWeight: 400 }}> / {status.bundleSearchesQuota}</span>
          </div>
          <div style={{ fontSize: 11, color: '#94A3B8' }}>searches this billing period</div>
        </div>
        <div style={{ height: 8, background: '#F1F5F9', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${usagePct}%`, background: usageColor, transition: 'width 0.3s' }}/>
        </div>
        {usagePct >= 75 && usagePct < 100 && (
          <div style={{ marginTop: 10, fontSize: 11, color: '#F59E0B' }}>
            You've used {Math.round(usagePct)}% of your monthly AI bundle searches. Upgrade for more.
          </div>
        )}
        {usagePct >= 100 && (
          <div style={{ marginTop: 10, fontSize: 11, color: '#EF4444' }}>
            You've hit your monthly limit. Resets {status.currentPeriodEnd ? new Date(status.currentPeriodEnd).toLocaleDateString() : 'at renewal'}, or upgrade now.
          </div>
        )}
      </div>

      {/* Seat allocation. Paid seats are managed HERE, deliberately, not as a
          side effect of adding or removing team members — see the rationale on
          /api/billing/seats. The common case for removing someone is replacing
          them, so silently decrementing would churn their invoice for no net
          change. */}
      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24 }}>
        <div style={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Team seats</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <div style={{ fontSize: 22, fontWeight: 600, color: BRAND.colors.dark }}>
            {status.seats.active}<span style={{ fontSize: 14, color: '#94A3B8', fontWeight: 400 }}> / {status.seats.allowed ?? (status.seats.included + status.seats.extra)} seats used</span>
          </div>
          {isAdmin && status.seats.canChange && (
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <button onClick={() => changeSeats(Math.max(0, (status.seats.extra || 0) - 1))}
                disabled={seatBusy || (status.seats.unused || 0) < 1}
                title={(status.seats.unused || 0) < 1 ? 'All paid seats are in use — remove a member first' : 'Release one paid seat'}
                style={{ width:26, height:26, borderRadius:7, border:'1px solid #E2E8F0', background:'#fff',
                  cursor: (seatBusy || (status.seats.unused||0) < 1) ? 'not-allowed' : 'pointer',
                  opacity: (seatBusy || (status.seats.unused||0) < 1) ? 0.4 : 1,
                  fontSize:15, lineHeight:1, fontFamily:'inherit' }}>−</button>
              <span style={{ fontSize:12, color:'#64748B', minWidth:64, textAlign:'center' }}>
                {status.seats.extra || 0} extra
              </span>
              <button onClick={() => changeSeats((status.seats.extra || 0) + 1)} disabled={seatBusy}
                title="Add a paid seat ($10/mo)"
                style={{ width:26, height:26, borderRadius:7, border:'1px solid #E2E8F0', background:'#fff',
                  cursor: seatBusy ? 'wait' : 'pointer', fontSize:15, lineHeight:1, fontFamily:'inherit' }}>+</button>
            </div>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#64748B', marginTop: 6 }}>
          {status.seats.included} included with {PLAN_NAMES[status.plan]} plan
          {status.seats.extra > 0 && ` · ${status.seats.extra} extra seat${status.seats.extra > 1 ? 's' : ''} at $10/mo each`}
        </div>

        {/* Paying for seats nobody is using. Prompted, never automatic — they
            may be between hires. */}
        {isAdmin && status.seats.canChange && (status.seats.unused || 0) > 0 && (
          <div style={{ marginTop:14, background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:9, padding:'12px 14px' }}>
            <div style={{ fontSize:12, color:'#92400E', lineHeight:1.6 }}>
              {/* Only EXTRA seats are separately billable. Seats included with
                  the plan cost nothing to leave empty, so counting them as
                  "seats you're paying for" overstated it — a Growth customer
                  with 1 person and 1 extra seat was told they were paying for
                  3 unused seats when the real waste is 1. */}
              You have <strong>{status.seats.unused} unused seat{status.seats.unused > 1 ? 's' : ''}</strong>.
              {(status.seats.extra || 0) > 0
                ? ` ${Math.min(status.seats.unused, status.seats.extra)} of ${status.seats.unused > 1 ? 'them is' : 'them is'} a paid extra — releasing ${Math.min(status.seats.unused, status.seats.extra) > 1 ? 'them' : 'it'} would cut $${Math.min(status.seats.unused, status.seats.extra) * 10}/mo from your next invoice.`
                : ' They\u2019re included with your plan at no extra cost — invite someone when you\u2019re ready.'}
            </div>
            {(status.seats.extra || 0) > 0 && (
              <button onClick={() => changeSeats(Math.max(0, (status.seats.extra || 0) - Math.min(status.seats.unused, status.seats.extra)))}
                disabled={seatBusy}
                style={{ marginTop:10, background:'#fff', border:'1px solid #FCD34D', borderRadius:7,
                  padding:'7px 14px', fontSize:12, color:'#92400E', fontWeight:600,
                  cursor: seatBusy ? 'wait' : 'pointer', fontFamily:'inherit' }}>
                {seatBusy ? 'Updating…' : `Release ${Math.min(status.seats.unused, status.seats.extra)} seat${Math.min(status.seats.unused, status.seats.extra) > 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        )}

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #F1F5F9', fontSize: 12, color: '#64748B' }}>
          Add or remove team members on the <a href="#team" style={{ color: G, textDecoration: 'none', fontWeight: 500 }}>Team page</a>.
          {status.seats.canChange
            ? ' Removing a member frees their seat but keeps the seat paid for, so you can replace them without a billing change — release it here when you don\u2019t need it.'
            : ' Seat changes require an active paid subscription.'}
        </div>
      </div>
    </div>
  );
}
